// Helpers that turn the saved Public Website settings into what the site renders.
import { useEffect } from "react";
import { API } from "@/lib/api";

export const HOME_SECTIONS = [
  ["founder_quote", "Introduction"],
  ["kitchen_tour", "3D kitchen tour"],
  ["why", "Three reasons (stacked rows)"],
  ["stats", "Counters"],
  ["materials", "Surfaces carousel"],
  ["location", "Showroom location"],
  ["gallery", "Featured work list"],
  ["reflect", "Designs reflect you"],
  ["services", "Image + ticker band"],
  ["workflow", "How we work"],
  ["testimonials", "Testimonials"],
  ["cta", "Contact / quote form"],
  ["suppliers", "Brands we stock"],
  ["featured", "Slab carousel"],
];

export const sectionOrder = (cfg) => {
  const all = HOME_SECTIONS.map(([k]) => k);
  const saved = (cfg?.order || []).filter((k) => all.includes(k));
  if (!saved.length) return all;
  // Insert any new/missing sections at their natural spot (from HOME_SECTIONS),
  // right after the nearest preceding section that the shop already ordered.
  const result = [...saved];
  all
    .filter((k) => !saved.includes(k))
    .forEach((k) => {
      const natural = all.indexOf(k);
      let at = result.length;
      for (let i = natural - 1; i >= 0; i -= 1) {
        const pos = result.indexOf(all[i]);
        if (pos !== -1) {
          at = pos + 1;
          break;
        }
      }
      result.splice(at, 0, k);
    });
  return result;
};

export const siteImg = (value, fallback = "") => {
  if (!value) return fallback;
  return value.startsWith("http") ? value : `${API}/public/site/image/${value}`;
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const addScript = (id, src, inline) => {
  if (document.getElementById(id)) return;
  const el = document.createElement("script");
  el.id = id;
  if (src) {
    el.src = src;
    el.async = true;
  }
  if (inline) el.innerHTML = inline;
  document.head.appendChild(el);
};

let pageHead = null;

export const usePageHead = ({ title, description, suffix, image, type }) => {
  useEffect(() => {
    if (!title) return undefined;
    const full = suffix ? `${title} | ${suffix}` : title;
    pageHead = { title: full, description };
    document.title = full;
    setMeta("property", "og:title", full);
    setMeta("property", "og:type", type || "website");
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
      setMeta("name", "twitter:card", "summary_large_image");
      setMeta("name", "twitter:title", full);
    }
    setMeta("property", "og:url", window.location.href.split("#")[0]);
    let link = document.head.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", window.location.href.split("#")[0]);
    return () => {
      pageHead = null;
    };
  }, [title, description, suffix, image, type]);
};

export const useSiteHead = (cfg, shop) => {
  useEffect(() => {
    if (!cfg) return;
    const seo = cfg.seo || {};
    const g = cfg.google || {};
    if (seo.title && !pageHead) {
      document.title = seo.title;
      setMeta("property", "og:title", seo.title);
    }
    if (seo.description && !pageHead) {
      setMeta("name", "description", seo.description);
      setMeta("property", "og:description", seo.description);
    }
    const og = siteImg(seo.og_image);
    if (og) setMeta("property", "og:image", og);
    setMeta("name", "twitter:card", og ? "summary_large_image" : "summary");
    if ((seo.keywords || "").trim()) {
      setMeta("name", "keywords", seo.keywords.trim());
    }
    if (seo.ai_optimized !== false) {
      setMeta(
        "name",
        "robots",
        "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      );
    }
    // Search Console ownership: the meta-tag method
    if ((g.site_verification || "").trim()) {
      setMeta("name", "google-site-verification", g.site_verification.trim());
    }

    const a = cfg.analytics || {};
    const gtagIds = [a.ga_id, g.ads_id].map((v) => (v || "").trim()).filter(Boolean);
    if (gtagIds.length) {
      addScript(
        "site-ga-src",
        `https://www.googletagmanager.com/gtag/js?id=${gtagIds[0]}`,
      );
      addScript(
        "site-ga-init",
        null,
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());${gtagIds
          .map((id) => `gtag('config','${id}');`)
          .join("")}`,
      );
    }
    if (a.gtm_id) {
      addScript(
        "site-gtm",
        null,
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${a.gtm_id}');`,
      );
    }
    if (a.meta_pixel) {
      addScript(
        "site-meta-pixel",
        null,
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${a.meta_pixel}');fbq('track','PageView');`,
      );
    }

    // LocalBusiness structured data: what Google uses to tie the site to the Business Profile
    const ld = document.getElementById("site-ldjson");
    if (g.structured_data === false || !shop?.name) {
      if (ld) ld.remove();
      return;
    }
    const footer = cfg.footer || {};
    const sameAs = [
      footer.instagram,
      footer.facebook,
      footer.twitter,
      footer.youtube,
      g.business_profile_url,
      ...String(g.same_as || "")
        .split(/[\n,]/)
        .map((s) => s.trim()),
    ].filter(Boolean);
    const siteUrl = (g.site_url || "").trim() || window.location.origin;
    const data = {
      "@context": "https://schema.org",
      "@type": g.business_type || "HomeAndConstructionBusiness",
      "@id": `${siteUrl}/#business`,
      name: shop.name,
      url: siteUrl,
      telephone: shop.phone || undefined,
      email: shop.email || undefined,
      address: shop.address
        ? { "@type": "PostalAddress", streetAddress: shop.address }
        : undefined,
      geo:
        g.latitude && g.longitude
          ? {
              "@type": "GeoCoordinates",
              latitude: Number(g.latitude),
              longitude: Number(g.longitude),
            }
          : undefined,
      openingHours: (shop.hours_lines || []).length ? shop.hours_lines : undefined,
      priceRange: g.price_range || undefined,
      image: og || undefined,
      logo: siteImg(cfg.branding?.logo_regular_light) || undefined,
      hasMap: g.business_profile_url || undefined,
      sameAs: sameAs.length ? sameAs : undefined,
      description: seo.description || undefined,
    };
    Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
    let el = ld;
    if (!el) {
      el = document.createElement("script");
      el.id = "site-ldjson";
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  }, [cfg, shop]);
};
